import { useInfiniteQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api } from "@config/axios/axios.config";
import { QUERY_KEYS, SERVER_ROUTES } from "@constants/api.routes";
import type {
	EquipmentResponse,
	Equipment,
	CreateEquipmentRequest,
	CreateEquipmentResponse,
	UpdateEquipmentRequest,
	UpdateEquipmentResponse,
} from "../types/types";
import { showNotification } from "@libs/notistack/notificationStack";
import type { AxiosErrorResponse } from "../../../../../types/error.types";
import { HTTP_STATUS } from "@constants/http.status.code";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { t } from "i18next";

// Types for cache data
interface CachePage {
	data: {
		data: Equipment[];
		pagination?: {
			currentPage: number;
			totalPages: number;
			totalItems: number;
			hasNextPage: boolean;
		};
	};
}
interface CacheData {
	pages: CachePage[];
}

// Get Equipments Infinite Query
export const useGetEquipmentsInfinite = (limit: number = 10, search?: string) => {
	const normalizedSearch = search?.trim() ?? "";
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS?.INSTITUTES?.EQUIPMENTS, "infinite", normalizedSearch],
		queryFn: async ({ pageParam = 1 }): Promise<EquipmentResponse> => {
			try {
				const response = await api.get<EquipmentResponse>(
					SERVER_ROUTES.EQUIPMENTS.GET(limit, pageParam, normalizedSearch || undefined),
				);

				// Handle different possible response structures
				const responseData = response?.data;

				// If the response doesn't have the expected nested structure, try to normalize it
				if (responseData?.success && responseData?.data) {
					// Check if data is already in the expected format
					if (Array.isArray(responseData?.data)) {
						// If data is directly an array, wrap it in the expected structure
						return {
							...responseData,
							data: {
								data: responseData?.data,
								pagination: {
									currentPage: pageParam,
									perPage: limit,
									totalItems: responseData?.data?.length,
									totalPages: 1,
									hasNextPage: false,
									hasPreviousPage: false,
								},
							},
						};
					}

					// If data has the expected nested structure, return as is
					if (responseData?.data?.data && responseData?.data?.pagination) {
						return responseData;
					}
				}

				// If none of the above, return the original response
				return responseData;
			} catch (error) {
				console.error("Equipment API Error:", error);
				showNotification({
					message:
						((error as AxiosErrorResponse)?.response?.data?.message as string) ||
						t("feature.courseBuilder.equipments.errors.failedToLoad"),
					severity: SNACKBAR_SEVERITY.ERROR,
				});
				throw error;
			}
		},
		getNextPageParam: lastPage => {
			// Add null checks to prevent undefined errors
			if (!lastPage) {
				return undefined;
			}

			// Handle different possible response structures
			let pagination;

			// Check for nested structure first
			if (lastPage?.data && lastPage?.data?.pagination) {
				pagination = lastPage?.data?.pagination;
			}
			// Check for direct structure (type assertion for flexibility)
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			else if ((lastPage as any)?.pagination) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				pagination = (lastPage as any)?.pagination;
			}
			// If no pagination found, return undefined
			else {
				return undefined;
			}

			// Check if we have the required pagination properties
			if (pagination && pagination.hasNextPage !== undefined) {
				return pagination.hasNextPage ? pagination.currentPage + 1 : undefined;
			}

			// Fallback: check if there are more pages based on currentPage and totalPages
			if (pagination && pagination.currentPage && pagination.totalPages) {
				return pagination.currentPage < pagination.totalPages ? pagination.currentPage + 1 : undefined;
			}

			return undefined;
		},
		initialPageParam: 1,
		placeholderData: keepPreviousData,
		staleTime: 0, // 0 minutes - prevent unnecessary refetches
		gcTime: 10 * 60 * 1000, // 10 minutes
		refetchOnWindowFocus: false, // Prevent refetch on window focus
		refetchOnMount: false, // Prevent refetch on component mount if data exists
		retry: 0, // Disable retries to prevent multiple error notifications
	});
};
// Delete Equipment Mutation
export const useDeleteEquipment = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (equipmentId: string): Promise<{ success: boolean; message: string; data: null }> => {
			const response = await api.delete(SERVER_ROUTES.EQUIPMENTS.DELETE(equipmentId));
			return response.data;
		},
		onSuccess: (_, equipmentId) => {
			// Invalidate and refetch equipment queries
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS?.INSTITUTES?.EQUIPMENTS],
			});

			// Optimistically update the cache by removing the deleted equipment
			queryClient.setQueryData([QUERY_KEYS?.INSTITUTES?.EQUIPMENTS, "infinite"], (oldData: CacheData | undefined) => {
				if (!oldData || !oldData?.pages) return oldData;

				return {
					...oldData,
					pages: oldData?.pages?.map((page: CachePage) => {
						if (!page || !page?.data || !page?.data?.data) return page;

						const filteredData = page?.data?.data?.filter((equipment: Equipment) => equipment?.uuid !== equipmentId);

						// Only update if data actually changed
						if (filteredData?.length === page?.data?.data?.length) return page;

						return {
							...page,
							data: {
								...page?.data,
								data: filteredData,
							},
						};
					}),
				};
			});
		},
		onError: (error: AxiosErrorResponse) => {
			if (error?.response?.data?.statusCode === HTTP_STATUS.CONFLICT) {
				showNotification({
					message:
						(error?.response?.data?.message as string) || t("feature.courseBuilder.equipments.errors.failedToDelete"),
					severity: SNACKBAR_SEVERITY?.ERROR,
				});
			}
		},
	});
};

// Create Equipment Mutation
export const useCreateEquipment = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (equipmentData: CreateEquipmentRequest): Promise<CreateEquipmentResponse> => {
			const response = await api.post<CreateEquipmentResponse>(SERVER_ROUTES.EQUIPMENTS.CREATE, equipmentData);
			return response.data;
		},
		onSuccess: data => {
			// Invalidate and refetch equipment queries
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS?.INSTITUTES?.EQUIPMENTS],
			});

			// Optimistically update the cache by adding the new equipment
			queryClient.setQueryData([QUERY_KEYS?.INSTITUTES?.EQUIPMENTS, "infinite"], (oldData: CacheData | undefined) => {
				if (!oldData || !oldData?.pages) return oldData;

				return {
					...oldData,
					pages: oldData?.pages?.map((page: CachePage, index: number) => {
						// Add to first page
						if (index === 0 && page && page?.data && page?.data?.data) {
							// Check if equipment already exists to prevent duplicates
							const exists = page?.data?.data?.some((equipment: Equipment) => equipment?.uuid === data?.data?.uuid);
							if (exists) return page;

							return {
								...page,
								data: {
									...page?.data,
									data: [data.data, ...page.data.data],
								},
							};
						}
						return page;
					}),
				};
			});
		},

		onError: (error: AxiosErrorResponse) => {
			if (error?.response?.data?.statusCode === HTTP_STATUS.CONFLICT) {
				showNotification({
					message:
						(error?.response?.data?.message as string) || t("feature.courseBuilder.equipments.errors.failedToCreate"),
					severity: SNACKBAR_SEVERITY?.ERROR,
				});
			}
		},
	});
};

// Update Equipment Mutation
export const useUpdateEquipment = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			equipmentId,
			equipmentData,
		}: {
			equipmentId: string;
			equipmentData: UpdateEquipmentRequest;
		}): Promise<UpdateEquipmentResponse> => {
			const response = await api.patch<UpdateEquipmentResponse>(
				SERVER_ROUTES.EQUIPMENTS.UPDATE(equipmentId),
				equipmentData,
			);
			return response.data;
		},
		onSuccess: (data, { equipmentId }) => {
			// Invalidate and refetch equipment queries
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS?.INSTITUTES?.EQUIPMENTS],
			});

			// Optimistically update the cache by updating the equipment
			queryClient.setQueryData([QUERY_KEYS?.INSTITUTES?.EQUIPMENTS, "infinite"], (oldData: CacheData | undefined) => {
				if (!oldData || !oldData?.pages) return oldData;

				return {
					...oldData,
					pages: oldData?.pages?.map((page: CachePage) => {
						if (!page || !page?.data || !page?.data?.data) return page;

						const updatedData = page?.data?.data?.map((equipment: Equipment) => {
							if (equipment?.uuid === equipmentId) {
								// Only update if data actually changed
								if (JSON.stringify(equipment) === JSON.stringify(data.data)) {
									return equipment;
								}
								return data.data;
							}
							return equipment;
						});

						// Only update if data actually changed
						if (JSON.stringify(updatedData) === JSON.stringify(page?.data?.data)) return page;

						return {
							...page,
							data: {
								...page?.data,
								data: updatedData,
							},
						};
					}),
				};
			});
		}
	});
};
